import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

import { API_BASE_URL } from '../utils/urls.js';

const ClientDashboard = () => {
    const [cases, setCases] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const user = JSON.parse(localStorage.getItem('user'));
    const clientId = user?.id;

    const formatLabel = (value) => {
        if (!value) return "";
        return value
            .toString()
            .replace(/_/g, " ")
            .toLowerCase()
            .replace(/\b\w/g, (char) => char.toUpperCase());
    };

    useEffect(() => {
        const fetchData = async () => {
            const token = localStorage.getItem('token');
            if (!token || !clientId) {
                setError('Authentication token or client ID not found.');
                setLoading(false);
                return;
            }

            try {
                const [casesRes, invoicesRes] = await Promise.all([
                    axios.get(`${API_BASE_URL}/cases/client-cases`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }),
                    axios.get(`${API_BASE_URL}/invoices/client/${clientId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }),
                ]);

                setCases(casesRes.data.data);
                setInvoices(invoicesRes.data);
            } catch (err) {
                console.error('Error fetching dashboard data:', err);
                setError('Failed to load dashboard data. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [clientId]);

    if (loading) {
        return <div className="p-6 text-center text-gray-500">Loading dashboard...</div>;
    }

    if (error) {
        return <div className="p-6 text-center text-red-500">{error}</div>;
    }

    return (
        <div className="p-4 sm:p-6 space-y-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Client Dashboard</h1>

            {/* My Cases Section */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
                <h2 className="text-xl font-bold mb-4 text-gray-800">My Cases</h2>
                <div className="overflow-x-auto">
                    <table className="min-w-full hidden sm:table divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Case Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Case Type</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Work Status</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {cases.length > 0 ? (
                                cases.map(caseItem => (
                                    <tr key={caseItem.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <Link
                                                to={caseItem.is_pi ? `/client/pi-cases/${caseItem.id}` : `/client/cases/${caseItem.id}`}
                                                className="text-blue-600 hover:underline"
                                            >
                                                {caseItem.case_name}
                                            </Link>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {caseItem.is_pi ? "Private Investigation" : "Process Serve"}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{caseItem.status}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(caseItem.due_date).toLocaleDateString()}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {caseItem.is_pi
                                                ? formatLabel(caseItem.fieldwork_status) ?? '-'
                                                : formatLabel(caseItem.server_work_status) ?? '-'}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="px-6 py-4 text-center text-gray-500">No cases found.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    {/* Mobile Card View */}
                    <div className="sm:hidden space-y-4">
                        {cases.length > 0 ? (
                            cases.map(caseItem => (
                                <div key={caseItem.id} className="bg-white p-4 border border-gray-200 rounded-lg shadow-sm">
                                    <h3 className="text-lg font-bold">
                                        <Link
                                            to={caseItem.is_pi ? `/client/pi-cases/${caseItem.id}` : `/client/cases/${caseItem.id}`}
                                            className="text-blue-600 hover:underline"
                                        >
                                            {caseItem.case_name}
                                        </Link>
                                    </h3>
                                    <p className="text-sm text-gray-600 mt-1"><span className="font-semibold">Status:</span> {caseItem.status}</p>
                                    <p className="text-sm text-gray-600"><span className="font-semibold">Due Date:</span> {new Date(caseItem.due_date).toLocaleDateString()}</p>
                                    <p className="text-sm text-gray-600"><span className="font-semibold">Work Status:</span> {caseItem.is_pi ? caseItem.fieldwork_status ?? '-' : caseItem.server_work_status ?? '-'}</p>
                                </div>
                            ))
                        ) : (
                            <div className="p-4 text-center text-gray-500">No cases found.</div>
                        )}
                    </div>
                </div>
            </div>

            {/* My Invoices Section */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
                <h2 className="text-xl font-bold mb-4 text-gray-800">My Invoices</h2>
                <div className="overflow-x-auto">
                    <table className="min-w-full hidden sm:table divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice Number</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {invoices.length > 0 ? (
                                invoices.map(invoice => (
                                    <tr key={invoice.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            <Link
                                                to={`/invoices/client/${clientId}/${invoice.id}`}
                                                className="text-blue-600 hover:underline"
                                            >
                                                {invoice.invoice_number}
                                            </Link>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${parseFloat(invoice.total_amount).toFixed(2)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(invoice.due_date).toLocaleDateString()}</td>
                                       <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
  <span
    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full
      ${
        invoice.status === "paid"
          ? "bg-green-100 text-green-800"
          : "bg-red-100 text-red-800"
      }
    `}
  >
    {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
  </span>
</td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="4" className="px-6 py-4 text-center text-gray-500">No invoices found.</td></tr>
                            )}
                        </tbody>
                    </table>

                    {/* Mobile Card View */}
                    <div className="sm:hidden space-y-4">
                        {invoices.length > 0 ? (
                            invoices.map(invoice => (
                                <div key={invoice.id} className="bg-white p-4 border border-gray-200 rounded-lg shadow-sm">
                                    <h3 className="text-lg font-bold text-gray-900">Invoice #{invoice.invoice_number}</h3>
                                    <p className="text-sm text-gray-600 mt-1"><span className="font-semibold">Amount:</span> ${parseFloat(invoice.total_amount).toFixed(2)}</p>
                                    <p className="text-sm text-gray-600"><span className="font-semibold">Due Date:</span> {new Date(invoice.due_date).toLocaleDateString()}</p>
                                    <p className="text-sm text-gray-600 "><span className="font-semibold">Status:</span> <span class={`px-2 rounded-full ${
        invoice.status === "paid"
          ? "bg-green-100 text-green-800"
          : "bg-red-100 text-red-800"
      }`}>{formatLabel(invoice.status)}</span></p>
                                </div>
                            ))
                        ) : (
                            <div className="p-4 text-center text-gray-500">No invoices found.</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClientDashboard;
